import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { ArrowLeft, Printer, CheckCircle2, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { getAdminHeaders } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PrintLabel = ({ label, onPrinted }) => {
  const handlePrint = () => {
    const printWindow = window.open('', '_blank', 'width=400,height=200');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Etiqueta - ${label.child_name}</title>
        <style>
          @page { size: 90mm 30mm; margin: 0; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body { width: 90mm; height: 30mm; font-family: Arial, Helvetica, sans-serif; overflow: hidden; }
          .label { width: 90mm; height: 30mm; display: flex; align-items: center; padding: 1.5mm 2mm; gap: 1.5mm; }
          .qr-section { flex-shrink: 0; width: 22mm; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 0.5mm; }
          .qr-section svg { width: 20mm !important; height: 20mm !important; }
          .qr-label { font-size: 5pt; color: #666; text-align: center; }
          .divider { width: 0.3mm; height: 22mm; background: #ddd; flex-shrink: 0; }
          .info-section { flex: 1; min-width: 0; display: flex; flex-direction: column; justify-content: center; gap: 0.8mm; }
          .child-name { font-size: 12pt; font-weight: 900; color: #000; line-height: 1.1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .child-id { font-size: 7pt; font-weight: 700; color: #333; letter-spacing: 0.5px; }
          .grupo { font-size: 7.5pt; font-weight: 700; color: #1A4D2E; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
          .meta-row { display: flex; align-items: center; gap: 3mm; font-size: 6.5pt; color: #555; }
          .meta-row span { white-space: nowrap; }
          .logos-section { flex-shrink: 0; width: 14mm; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 1mm; }
          .logos-section img { width: 12mm; height: auto; object-fit: contain; }
        </style>
      </head>
      <body>
        <div class="label">
          <div class="qr-section">
            <svg id="qr-placeholder"></svg>
            <div class="qr-label">Raizes</div>
          </div>
          <div class="divider"></div>
          <div class="info-section">
            <div class="child-name">${label.child_name}</div>
            <div class="child-id">${label.child_codigo || ''}</div>
            ${label.grupo_nome ? `<div class="grupo">${label.grupo_nome}</div>` : ''}
            <div class="meta-row">
              <span>${label.date}</span>
              <span>${label.checkin_time}</span>
              ${label.pontualidade ? '<span style="color:#1A4D2E;font-weight:700">Pontual</span>' : ''}
            </div>
          </div>
          <div class="logos-section">
            <img src="${window.location.origin}/logos/logo_raizes_dark.png" alt="Raizes" />
            <img src="${window.location.origin}/logos/logo_clubinho.png" alt="Clubinho" />
          </div>
        </div>
        <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"><\/script>
        <script>
          const el = document.getElementById('qr-placeholder');
          QRCode.toDataURL('${label.qr_code}', { width: 200, margin: 0 }, function(err, url) {
            el.outerHTML = '<img src="' + url + '" style="width:20mm;height:20mm;" />';
            setTimeout(function() { window.print(); window.close(); }, 400);
          });
        <\/script>
      </body>
      </html>
    `);
    printWindow.document.close();
    onPrinted(label.checkin_id);
  };

  useEffect(() => {
    handlePrint();
  }, []);

  return null;
};

const EstacaoImpressao = () => {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);
  const [connected, setConnected] = useState(true);
  const [autoPrint, setAutoPrint] = useState(true);
  const [printedCount, setPrintedCount] = useState(0);
  const [printing, setPrinting] = useState(null);
  const [printerIp, setPrinterIp] = useState(localStorage.getItem('printer_ip') || '');
  const [printerStatus, setPrinterStatus] = useState(null); // null | 'testing' | 'online' | 'offline'
  const [showConfig, setShowConfig] = useState(false);
  const printingRef = useRef(false);

  const fetchQueue = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/checkin/print-queue`, { headers: getAdminHeaders() });
      setQueue(res.data.queue || []);
      setConnected(true);
    } catch {
      setConnected(false);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);

  useEffect(() => {
    const interval = setInterval(fetchQueue, 3000);
    return () => clearInterval(interval);
  }, [fetchQueue]);

  useEffect(() => {
    if (!autoPrint || queue.length === 0 || printingRef.current) return;
    printingRef.current = true;
    setPrinting(queue[0]);
  }, [queue, autoPrint]);

  const handlePrinted = async (checkinId) => {
    try {
      await axios.post(`${API}/checkin/mark-printed/${checkinId}`, {}, { headers: getAdminHeaders() });
    } catch { }
    setPrintedCount(prev => prev + 1);
    setPrinting(null);
    printingRef.current = false;
    fetchQueue();
  };

  const handleManualPrint = (label) => {
    setPrinting(label);
    printingRef.current = true;
  };

  const testPrinterConnection = async () => {
    if (!printerIp.trim()) { toast.error('Digite o IP da impressora'); return; }
    localStorage.setItem('printer_ip', printerIp.trim());
    setPrinterStatus('testing');
    try {
      // Try to reach the printer's web interface
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5000);
      await fetch(`http://${printerIp.trim()}/`, { mode: 'no-cors', signal: controller.signal });
      clearTimeout(timeout);
      setPrinterStatus('online');
      toast.success('Impressora acessivel na rede!');
    } catch {
      setPrinterStatus('offline');
      toast.error('Nao foi possivel conectar. Verifique o IP e a rede Wi-Fi.');
    }
  };

  return (
    <div className="min-h-screen bg-[#0D1117]">
      {/* Auto-print trigger */}
      {printing && <PrintLabel label={printing} onPrinted={handlePrinted} />}

      {/* Header */}
      <div className="bg-[#1A4D2E] text-white px-6 py-5">
        <div className="max-w-4xl mx-auto">
          <button onClick={() => navigate(-1)} className="hover:bg-white/10 rounded-full p-2 transition-all mb-2" data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Printer className="w-7 h-7" />
              <div>
                <h1 className="font-nunito font-bold text-2xl">Estacao de Impressao</h1>
                <p className="font-inter text-white/60 text-sm">Conecte este notebook a impressora Brother QL-810W</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {connected ? (
                <span className="flex items-center gap-1.5 text-green-300 text-xs font-semibold"><Wifi className="w-4 h-4" /> Conectado</span>
              ) : (
                <span className="flex items-center gap-1.5 text-red-300 text-xs font-semibold"><WifiOff className="w-4 h-4" /> Desconectado</span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6 space-y-4">
        {/* Controls */}
        <div className="flex items-center justify-between bg-[#161B22] rounded-2xl p-4 border border-[#30363D]">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setAutoPrint(!autoPrint)}
              data-testid="toggle-auto-print"
              className={`relative w-12 h-7 rounded-full transition-all ${autoPrint ? 'bg-green-500' : 'bg-gray-600'}`}
            >
              <span className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${autoPrint ? 'left-5' : 'left-0.5'}`} />
            </button>
            <div>
              <p className="text-white text-sm font-semibold">Impressao automatica</p>
              <p className="text-gray-500 text-xs">{autoPrint ? 'Etiquetas serao impressas ao escanear' : 'Clique para imprimir manualmente'}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-green-400 font-bold text-2xl">{printedCount}</p>
            <p className="text-gray-500 text-xs">impressas hoje</p>
          </div>
        </div>

        {/* Status */}
        {queue.length === 0 ? (
          <Card className="bg-[#161B22] border border-[#30363D] rounded-2xl p-12 text-center" data-testid="empty-queue">
            <div className="relative mx-auto w-16 h-16 mb-4">
              <Printer className="w-16 h-16 text-gray-700" />
              <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse" />
            </div>
            <p className="text-gray-400 font-semibold text-lg">Aguardando check-ins...</p>
            <p className="text-gray-600 text-sm mt-1">Quando o voluntario escanear um QR Code, a etiqueta sera impressa automaticamente</p>
          </Card>
        ) : (
          <div className="space-y-2">
            <p className="text-amber-400 text-sm font-semibold">{queue.length} etiqueta{queue.length !== 1 ? 's' : ''} na fila</p>
            {queue.map(label => (
              <Card key={label.checkin_id} className="bg-[#161B22] border border-[#30363D] rounded-xl p-4 flex items-center justify-between" data-testid={`queue-item-${label.checkin_id}`}>
                <div className="flex items-center gap-3">
                  <QRCodeSVG value={label.qr_code} size={40} level="M" />
                  <div>
                    <p className="text-white font-bold text-sm">{label.child_name}</p>
                    <p className="text-gray-500 text-xs">{label.child_codigo} - {label.grupo_nome}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-gray-500 text-xs">{label.checkin_time}</span>
                  {!autoPrint && (
                    <button onClick={() => handleManualPrint(label)} data-testid={`manual-print-${label.checkin_id}`}
                      className="bg-[#1A4D2E] hover:bg-[#163f25] text-white px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5">
                      <Printer className="w-3.5 h-3.5" /> Imprimir
                    </button>
                  )}
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Printer Configuration */}
        <Card className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
          <button onClick={() => setShowConfig(!showConfig)} className="w-full flex items-center justify-between" data-testid="toggle-config">
            <p className="text-gray-400 text-sm font-semibold flex items-center gap-2">
              <Printer className="w-4 h-4" /> Configuracao da Impressora
            </p>
            <div className="flex items-center gap-2">
              {printerStatus === 'online' && <span className="text-green-400 text-xs font-bold flex items-center gap-1"><Wifi className="w-3 h-3" /> Online</span>}
              {printerStatus === 'offline' && <span className="text-red-400 text-xs font-bold flex items-center gap-1"><WifiOff className="w-3 h-3" /> Offline</span>}
              <span className="text-gray-600 text-xs">{showConfig ? 'Fechar' : 'Abrir'}</span>
            </div>
          </button>

          {showConfig && (
            <div className="mt-4 space-y-4">
              {/* Test connection */}
              <div>
                <label className="text-gray-500 text-xs font-semibold block mb-1">IP da Impressora Brother QL-810W</label>
                <div className="flex gap-2">
                  <input
                    data-testid="printer-ip-input"
                    type="text"
                    value={printerIp}
                    onChange={e => setPrinterIp(e.target.value)}
                    placeholder="192.168.1.100"
                    className="flex-1 bg-[#0D1117] border border-[#30363D] rounded-lg px-3 py-2 text-white text-sm focus:ring-2 focus:ring-[#1A4D2E] focus:border-transparent outline-none"
                  />
                  <button
                    onClick={testPrinterConnection}
                    disabled={printerStatus === 'testing'}
                    data-testid="test-printer-btn"
                    className="bg-[#1A4D2E] hover:bg-[#163f25] text-white px-4 py-2 rounded-lg text-sm font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  >
                    {printerStatus === 'testing' ? (
                      <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                    ) : (
                      <Wifi className="w-4 h-4" />
                    )}
                    Testar
                  </button>
                </div>
                {printerStatus === 'online' && (
                  <p className="text-green-400 text-xs mt-1.5">Impressora acessivel em {printerIp}. Certifique-se de que esta adicionada no sistema operacional.</p>
                )}
                {printerStatus === 'offline' && (
                  <p className="text-red-400 text-xs mt-1.5">Nao foi possivel conectar em {printerIp}. Verifique se a impressora esta ligada e na mesma rede Wi-Fi.</p>
                )}
              </div>

              {/* Step by step */}
              <div className="border-t border-[#30363D] pt-3">
                <p className="text-gray-500 text-xs font-semibold uppercase mb-2">Passo a Passo para Configurar</p>
                <ol className="text-gray-400 text-xs space-y-2 list-decimal pl-4">
                  <li><strong className="text-gray-300">Ligue a impressora</strong> — O LED de energia deve acender. Aguarde a inicializacao (LED Wi-Fi piscando).</li>
                  <li><strong className="text-gray-300">Conecte ao Wi-Fi</strong> — Use o app <span className="text-blue-400">Brother iPrint&Label</span> no celular ou o utilitario de configuracao no notebook para conectar a QL-810W na mesma rede Wi-Fi do notebook.</li>
                  <li><strong className="text-gray-300">Descubra o IP</strong> — No app Brother ou no painel do roteador, encontre o IP atribuido (ex: 192.168.1.100). Digite acima e clique "Testar".</li>
                  <li><strong className="text-gray-300">Adicione a impressora no SO</strong> — No notebook, va em Configuracoes {'>'} Impressoras {'>'} Adicionar. Selecione "Brother QL-810W" na lista de impressoras de rede.</li>
                  <li><strong className="text-gray-300">Configure como padrao</strong> — Defina a QL-810W como impressora padrao para que as etiquetas sejam impressas automaticamente.</li>
                  <li><strong className="text-gray-300">Teste a impressao</strong> — Imprima uma pagina de teste pelo SO para confirmar que a comunicacao esta funcionando.</li>
                </ol>
              </div>

              {/* Tips */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                <p className="text-amber-400 text-xs font-semibold mb-1">Dicas Importantes</p>
                <ul className="text-amber-300/70 text-xs space-y-1 list-disc pl-4">
                  <li>Use etiquetas DK-22205 (62mm continuo) na Brother QL-810W</li>
                  <li>O notebook e a impressora DEVEM estar na mesma rede Wi-Fi</li>
                  <li>Mantenha esta pagina aberta durante todo o encontro</li>
                  <li>Se a impressora desconectar, reinicie-a e teste novamente</li>
                </ul>
              </div>
            </div>
          )}
        </Card>

        {/* Quick instructions (always visible) */}
        <Card className="bg-[#161B22] border border-[#30363D] rounded-xl p-4">
          <p className="text-gray-500 text-xs font-semibold uppercase mb-2">Como funciona</p>
          <ol className="text-gray-400 text-xs space-y-1.5 list-decimal pl-4">
            <li>Mantenha esta pagina aberta no notebook conectado a impressora</li>
            <li>O voluntario escaneia o QR Code da crianca pelo celular</li>
            <li>A etiqueta aparece aqui e e impressa automaticamente</li>
            <li>Se a impressao automatica estiver desligada, clique em "Imprimir"</li>
          </ol>
        </Card>
      </div>
    </div>
  );
};

export default EstacaoImpressao;
